import React from 'react';
import PropTypes from 'prop-types';
import { noop } from '@lux/helpers';

import PlanCard from '../PlanCard';
import Spacing from '../Spacing';

import formatPrice from '../../helpers/formatPrice';
import mecId from '../../helpers/mec-id';
import isTagged from '../../helpers/isTagged';
import { planTagsConfig } from '../../config/plan-group-config';

const PlansList = ({
  plans,
  sizes,
  selectedPlanId,
  selectedSize,
  onPlanChange,
  onSizeChange,
  disabled
}) => {
  return plans.map(plan => {
    const {
      planId,
      planName,
      basePriceSchemaId,
      planDescSubLiner,
      monthlyCharges,
      originalPrice,
      rebate,
      data,
      talktime,
      sms,
      ribbon,
      tags
    } = plan;

    const identifier = mecId(basePriceSchemaId, planId);

    const renderSize = sizes.find(size => size.planId === plan.mecId);
    const { installmentPrice, monthlyTerm, partialUpfrontAmount } =
      renderSize || {};

    // Sets the default text to render for the price of the phone
    let phonePrice = isFinite(renderSize?.price)
      ? formatPrice(renderSize?.price)
      : '-';

    // Sets the price of the phone for an instalment plan
    if (installmentPrice && monthlyTerm && partialUpfrontAmount >= 0) {
      if (partialUpfrontAmount >= 0 && installmentPrice >= 0) {
        phonePrice = `${formatPrice(
          partialUpfrontAmount,
          0
        )} upfront, then ${formatPrice(installmentPrice)}/mth`;
      }

      if (!partialUpfrontAmount && installmentPrice >= 0) {
        phonePrice = `${formatPrice(installmentPrice)}/mth`;
      }
    }

    return (
      <Spacing key={identifier} bottom={2}>
        <PlanCard
          id={identifier}
          name={planName}
          active={selectedPlanId === identifier}
          ribbon={ribbon}
          indicativePrice={monthlyCharges}
          originalPrice={originalPrice}
          phonePrice={phonePrice}
          rebate={rebate && `BILL REBATE $${rebate}/mth`}
          data={data}
          talktime={talktime}
          sms={sms}
          size={renderSize}
          selectedSize={selectedSize}
          planDescSubLiner={planDescSubLiner}
          onSizeChange={onSizeChange}
          onPlanClick={() => onPlanChange(plan)}
          disabled={disabled}
          tags={tags}
        />
      </Spacing>
    );
  });
};

PlansList.defaultProps = {
  plans: [],
  sizes: [],
  selectedPlanId: '',
  selectedSize: '',
  onPlanChange: noop,
  onSizeChange: noop
};

const AllocationShape = PropTypes.shape({
  unit: PropTypes.string,
  quantity: PropTypes.string
});

PlansList.propTypes = {
  /** List of plans to render */
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      planId: PropTypes.string,
      planName: PropTypes.string,
      basePriceSchemaId: PropTypes.string,
      monthlyCharges: PropTypes.string,
      originalPrice: PropTypes.string,
      rebate: PropTypes.number,
      data: AllocationShape,
      talktime: AllocationShape,
      sms: AllocationShape,
      planDescSubLiner: PropTypes.string
    })
  ),
  /** List of sizes to render */
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      active: PropTypes.bool,
      available: PropTypes.bool,
      installmentPrice: PropTypes.number,
      monthlyTerm: PropTypes.string,
      partialUpfrontAmount: PropTypes.number,
      price: PropTypes.number
    })
  ),
  /** Selected plan id */
  selectedPlanId: PropTypes.string,
  /** Selected size */
  selectedSize: PropTypes.string,
  onPlanChange: PropTypes.func,
  onSizeChange: PropTypes.func
};

export default PlansList;
